import React, { useId } from 'react';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { cn } from './cn';
import { FOCUS_RING, type ControlSize } from './Button';

/**
 * Campos do design system Atmos.
 *
 * Todos compartilham a mesma casca: altura 34 (md), borda 1px, raio 8,
 * sombra sutil e o anel de foco único. O rótulo fica acima, a dica ou o
 * erro abaixo — e erro sempre vem com ícone, nunca só com a cor.
 */

const CONTROL_SIZE: Record<ControlSize, string> = {
  sm: 'h-7 text-[12.5px]',
  md: 'h-[34px] text-[13.5px]',
  lg: 'h-10 text-[14.5px]',
};

const SHELL =
  'w-full rounded-lg border bg-surface text-fg shadow-xs transition-[border-color,box-shadow] duration-[120ms] ' +
  'placeholder:text-muted disabled:cursor-not-allowed disabled:bg-sunken disabled:text-faint';

function shellBorder(invalid?: boolean): string {
  return invalid ? 'border-danger' : 'border-line';
}

/* ─── Wrapper com rótulo, dica e erro ─────────────────────── */
export interface FieldProps {
  label?: React.ReactNode;
  /** Marca visual de obrigatório (asterisco em danger). */
  required?: boolean;
  /** Texto auxiliar. Suprimido quando há erro. */
  hint?: React.ReactNode;
  error?: React.ReactNode;
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}

export function Field({ label, required, hint, error, htmlFor, className, children }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-[12.5px] font-medium text-fg-2">
          {label}
          {required && <span className="ml-0.5 text-danger">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className="flex items-center gap-1.5 text-[11.5px] text-danger">
          <AlertTriangle size={13} className="flex-none" />
          {error}
        </span>
      ) : (
        hint && <span className="text-[11.5px] text-muted">{hint}</span>
      )}
    </div>
  );
}

/* ─── Input ───────────────────────────────────────────────── */
// `prefix` é atributo nativo (string) — sobrescrevemos com ReactNode.
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  size?: ControlSize;
  invalid?: boolean;
  /** Ícone dentro do campo, à esquerda. */
  leadingIcon?: React.ReactNode;
  /** Ícone ou botão dentro do campo, à direita. */
  trailingIcon?: React.ReactNode;
  /** Afixo colado à esquerda, com fundo rebaixado (ex: "R$"). */
  prefix?: React.ReactNode;
  /** Números tabulares e alinhamento à direita — para valores. */
  numeric?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', invalid, leadingIcon, trailingIcon, prefix, numeric, className, ...props },
  ref,
) {
  const control = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        SHELL,
        shellBorder(invalid),
        CONTROL_SIZE[size],
        FOCUS_RING,
        'px-[11px]',
        leadingIcon && 'pl-[33px]',
        trailingIcon && 'pr-[33px]',
        numeric && 'text-right font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );

  if (prefix) {
    return (
      <div
        className={cn(
          'flex overflow-hidden rounded-lg border bg-surface shadow-xs',
          shellBorder(invalid),
          CONTROL_SIZE[size],
          'focus-within:border-primary focus-within:shadow-[0_0_0_3px_var(--ring)]',
        )}
      >
        <span className="grid place-items-center border-r border-line bg-sunken px-2.5 text-[12.5px] text-muted">
          {prefix}
        </span>
        <input
          ref={ref}
          aria-invalid={invalid || undefined}
          className={cn(
            'min-w-0 flex-1 border-0 bg-transparent px-[11px] text-fg outline-none placeholder:text-muted',
            numeric && 'text-right font-mono tabular-nums',
            className,
          )}
          {...props}
        />
      </div>
    );
  }

  if (!leadingIcon && !trailingIcon) return control;

  return (
    <div className="relative flex items-center">
      {leadingIcon && (
        <span className="pointer-events-none absolute left-[11px] flex text-muted">{leadingIcon}</span>
      )}
      {control}
      {trailingIcon && <span className="absolute right-[11px] flex text-muted">{trailingIcon}</span>}
    </div>
  );
});

/* ─── Select ──────────────────────────────────────────────── */
export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', invalid, className, children, ...props },
  ref,
) {
  return (
    <div className="relative flex items-center">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          SHELL,
          shellBorder(invalid),
          CONTROL_SIZE[size],
          FOCUS_RING,
          'cursor-pointer appearance-none pl-[11px] pr-8',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown size={14} className="pointer-events-none absolute right-[11px] text-muted" />
    </div>
  );
});

/* ─── Textarea ────────────────────────────────────────────── */
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, rows = 3, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        SHELL,
        shellBorder(invalid),
        FOCUS_RING,
        'resize-y px-[11px] py-[9px] text-[14px]',
        className,
      )}
      {...props}
    />
  );
});

/* ─── Checkbox / Radio ────────────────────────────────────── */
export function Checkbox({
  label,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: React.ReactNode }) {
  return (
    <label className={cn('flex cursor-pointer items-center gap-2 text-[13px] text-fg', className)}>
      <input
        type="checkbox"
        className="m-0 h-[15px] w-[15px] accent-[var(--primary)]"
        {...props}
      />
      {label}
    </label>
  );
}

export function Radio({
  label,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: React.ReactNode }) {
  return (
    <label className={cn('flex cursor-pointer items-center gap-2 text-[13px] text-fg', className)}>
      <input type="radio" className="m-0 h-[15px] w-[15px] accent-[var(--primary)]" {...props} />
      {label}
    </label>
  );
}

/* ─── Switch ──────────────────────────────────────────────── */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'flex items-center gap-2.5 rounded text-[13px] text-fg disabled:cursor-not-allowed disabled:opacity-50',
        FOCUS_RING,
        className,
      )}
    >
      <span
        className={cn(
          'flex h-5 w-[34px] flex-none rounded-full p-0.5 transition-colors duration-[120ms]',
          checked ? 'bg-primary' : 'bg-neutral-line',
        )}
      >
        <span
          className={cn(
            'h-4 w-4 rounded-full bg-white shadow-xs transition-transform duration-[160ms] ease-atmos',
            checked && 'translate-x-[14px]',
          )}
        />
      </span>
      {label}
    </button>
  );
}
